import { useState, useRef, useCallback } from 'react';
import { textToSpeech } from 'services/actions/chatbot_actions';

type SpeechPhase = 'idle' | 'loading' | 'playing';

export interface SpeechPlaybackControl {
  speak: (text: string) => Promise<void>;
  stop: () => void;
  isSpeaking: boolean;
  isLoading: boolean;
}

type WebAudioHandle = {
  ctx: AudioContext;
  source: AudioBufferSourceNode;
};

function pauseAudioElement(el: HTMLAudioElement) {
  try {
    el.pause();
    el.currentTime = 0;
    // eslint-disable-next-line no-empty
  } catch {}
}

function createBlobAudio(url: string): HTMLAudioElement {
  const audio = new Audio();
  audio.setAttribute('playsinline', 'true');
  audio.preload = 'auto';
  (audio as HTMLAudioElement & { playsInline?: boolean }).playsInline = true;
  audio.src = url;
  return audio;
}

async function ensurePlayableAudioBlob(blob: Blob): Promise<Blob> {
  const t = blob.type?.toLowerCase() ?? '';
  if (t.startsWith('audio/')) return blob;
  if (t.includes('json') || t.includes('text/html') || (t.startsWith('text/') && !t.includes('plain'))) {
    const detail = await blob.text();
    throw new Error(detail.length > 280 ? `${detail.slice(0, 280)}…` : detail);
  }
  if (!t || t === 'application/octet-stream') {
    const head = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
    const riff = head[0] === 0x52 && head[1] === 0x49 && head[2] === 0x46 && head[3] === 0x46;
    const ogg = head[0] === 0x4f && head[1] === 0x67 && head[2] === 0x67 && head[3] === 0x53;
    if (riff) return new Blob([blob], { type: 'audio/wav' });
    if (ogg) return new Blob([blob], { type: 'audio/ogg' });
    return new Blob([blob], { type: 'audio/mpeg' });
  }
  return blob;
}

function stopWebAudio(h: WebAudioHandle | null) {
  if (!h) return;
  try {
    h.source.stop(0);
    // eslint-disable-next-line no-empty
  } catch {}
  void h.ctx.close().catch(() => {});
}

function playBlobViaWebAudio(
  blob: Blob,
  session: number,
  sessionRef: React.MutableRefObject<number>,
  setPhase: React.Dispatch<React.SetStateAction<SpeechPhase>>,
  webAudioRef: React.MutableRefObject<WebAudioHandle | null>,
): Promise<void> {
  const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) {
    return Promise.reject(new Error('AudioContext indisponível'));
  }

  const ctx = new AC();
  webAudioRef.current = null;

  return (async () => {
    await ctx.resume();
    const ab = await blob.arrayBuffer();
    const audioBuffer = await ctx.decodeAudioData(ab.slice(0));
    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(ctx.destination);

    const handle: WebAudioHandle = { ctx, source };
    webAudioRef.current = handle;

    return new Promise<void>((resolve, reject) => {
      source.onended = () => {
        if (session !== sessionRef.current) {
          resolve();
          return;
        }
        webAudioRef.current = null;
        void ctx.close().catch(() => {});
        setPhase('idle');
        resolve();
      };
      try {
        source.start(0);
        if (session === sessionRef.current) {
          setPhase('playing');
        }
      } catch (e) {
        void ctx.close().catch(() => {});
        webAudioRef.current = null;
        reject(e);
      }
    });
  })();
}

export function useSpeech(): SpeechPlaybackControl {
  const [phase, setPhase] = useState<SpeechPhase>('idle');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const webAudioRef = useRef<WebAudioHandle | null>(null);
  const speakSessionRef = useRef(0);

  const isLoading = phase === 'loading';
  const isSpeaking = phase === 'playing';

  const stop = useCallback(() => {
    speakSessionRef.current += 1;

    stopWebAudio(webAudioRef.current);
    webAudioRef.current = null;

    if (audioRef.current) {
      pauseAudioElement(audioRef.current);
      audioRef.current = null;
    }
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    setPhase('idle');
  }, []);

  const speak = useCallback(async (text: string) => {
    if (!text?.trim()) return;

    const session = ++speakSessionRef.current;

    try {
      setPhase('loading');

      stopWebAudio(webAudioRef.current);
      webAudioRef.current = null;

      if (audioRef.current) {
        pauseAudioElement(audioRef.current);
        audioRef.current = null;
      }
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }

      const raw = await textToSpeech(text);
      if (session !== speakSessionRef.current) return;

      const blob = await ensurePlayableAudioBlob(raw);
      if (session !== speakSessionRef.current) return;

      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      const audio = createBlobAudio(url);

      audioRef.current = audio;

      const cleanupHtmlTrack = () => {
        if (session !== speakSessionRef.current) return;
        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current);
          objectUrlRef.current = null;
        }
        audioRef.current = null;
      };

      audio.addEventListener('playing', () => {
        if (session !== speakSessionRef.current) return;
        setPhase('playing');
      });

      audio.addEventListener('ended', () => {
        if (session !== speakSessionRef.current) return;
        cleanupHtmlTrack();
        setPhase('idle');
      });

      audio.addEventListener('error', () => {
        const code = audio.error?.code;
        const msg = audio.error?.message;
        if (code !== undefined) {
          console.warn('useSpeech: <audio> error (tentando Web Audio)', code, msg ?? '');
        }
      });

      try {
        await audio.play();
        if (session !== speakSessionRef.current) return;
        if (audio.error) {
          throw new Error('MEDIA_ERR após play()');
        }
        setPhase((p) => (p === 'idle' ? 'idle' : 'playing'));
      } catch {
        pauseAudioElement(audio);
        cleanupHtmlTrack();
        if (session !== speakSessionRef.current) return;
        try {
          await playBlobViaWebAudio(
            blob,
            session,
            speakSessionRef,
            setPhase,
            webAudioRef,
          );
        } catch (wErr) {
          console.error('useSpeech: reprodução falhou (HTMLAudio + Web Audio)', wErr);
          stopWebAudio(webAudioRef.current);
          webAudioRef.current = null;
          setPhase('idle');
        }
      }
    } catch (error: unknown) {
      if (session !== speakSessionRef.current) return;
      const err = error as { name?: string };
      if (err?.name !== 'AbortError') {
        console.error(error);
      }

      stopWebAudio(webAudioRef.current);
      webAudioRef.current = null;

      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }

      if (audioRef.current) {
        pauseAudioElement(audioRef.current);
        audioRef.current = null;
      }

      setPhase('idle');
    }
  }, []);

  return {
    speak,
    stop,
    isSpeaking,
    isLoading,
  };
}
